import React, { useState, useRef } from 'react';
import { Box, Container, Typography, Grid, List, ListItem, ListItemText, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, useTheme, useMediaQuery, Menu, MenuItem, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Composant de navigation avec onglets
const NavigationBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [scolariteMenuAnchor, setScolariteMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleScolariteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setScolariteMenuAnchor(event.currentTarget);
  };

  const handleScolariteMenuClose = () => {
    setScolariteMenuAnchor(null);
  };

  const handleContactsClick = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationItems = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    {
      label: 'SCOLARITE',
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ];

  return (
    <Box
      position="absolute"
      sx={{
        bgcolor: 'transparent',
        boxShadow: 'none',
        zIndex: 10,
        top: '60px',
        width: '100%'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-end', md: 'space-between' },
          alignItems: 'center',
          py: { xs: 1, md: 2 },
          px: { xs: 2, md: 0 },
          minHeight: { xs: 56, md: 64 }
        }}>
          {/* Logo - Masqué sur mobile */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              border: '2px solid #87CEEB',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'white',
              overflow: 'hidden',
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <img
                src="/img/pages/logologo.webp"
                alt="La Maison des Enfants La petite Académie"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          </Box>

          {/* Navigation Desktop */}
          {!isMobile && (
            <Box sx={{
              display: 'flex',
              gap: { md: 2, lg: 3 },
              alignItems: 'center'
            }}>
              {navigationItems.map((item) => (
                <Box key={item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <Typography
                        onClick={handleScolariteMenuOpen}
                        sx={{
                          color: item.label === 'SCOLARITE' ? '#FF9800' : 'white',
                          fontWeight: 600,
                          fontSize: { md: 12, lg: 14 },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          px: { md: 1.5, lg: 2 },
                          py: 1,
                          borderBottom: item.label === 'SCOLARITE' ? '2px solid #FF9800' : '2px solid transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#FF9800',
                            borderBottom: '2px solid #FF9800',
                          }
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Menu
                        anchorEl={scolariteMenuAnchor}
                        open={Boolean(scolariteMenuAnchor)}
                        onClose={handleScolariteMenuClose}
                        sx={{
                          '& .MuiPaper-root': {
                            bgcolor: 'rgba(23, 128, 194, 0.95)',
                            color: 'white',
                            mt: 1,
                            backdropFilter: 'blur(10px)',
                            minWidth: 200
                          }
                        }}
                      >
                        {item.submenu?.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={handleScolariteMenuClose}
                            sx={{
                              color: subItem.path === '/scolarite' ? '#FF9800' : 'white',
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : item.isScroll ? (
                    <Typography
                      onClick={handleContactsClick}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Menu Mobile */}
          {isMobile && (
            <>
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    bgcolor: 'rgba(23, 128, 194, 0.95)',
                    color: 'white',
                    mt: 1,
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                {navigationItems.map((item) => (
                  <Box key={item.label}>
                    {item.hasSubmenu ? (
                      <>
                        <MenuItem
                          onClick={handleScolariteMenuOpen}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 14,
                            textTransform: 'uppercase',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          {item.label}
                          <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </MenuItem>
                        <Menu
                          anchorEl={scolariteMenuAnchor}
                          open={Boolean(scolariteMenuAnchor)}
                          onClose={handleScolariteMenuClose}
                          sx={{
                            '& .MuiPaper-root': {
                              bgcolor: 'rgba(23, 128, 194, 0.95)',
                              color: 'white',
                              mt: 1,
                              backdropFilter: 'blur(10px)',
                              minWidth: 200
                            }
                          }}
                        >
                          {item.submenu?.map((subItem) => (
                            <MenuItem
                              key={subItem.label}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={() => {
                                handleScolariteMenuClose();
                                handleMobileMenuClose();
                              }}
                              sx={{
                                color: subItem.path === '/scolarite' ? '#FF9800' : 'white',
                                fontWeight: 600,
                                fontSize: 14,
                                textTransform: 'uppercase',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                }
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : item.isScroll ? (
                      <MenuItem
                        onClick={() => {
                          handleContactsClick();
                          handleMobileMenuClose();
                        }}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMobileMenuClose}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    )}
                  </Box>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

// Section Hero pour la page Scolarité
const ScolariteHero = () => {
  return (
    <Box sx={{
      width: '100%',
      height: '100vh',
      position: 'relative',
      bgcolor: '#1780c2',
      overflow: 'hidden'
    }}>
      {/* Image de fond */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/ac.jpg)', // Image de l'école
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: 1
      }} />

      {/* Overlay pour la lisibilité */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 2
      }} />

      {/* Contenu textuel */}
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 3,
        textAlign: 'center',
        width: { xs: '90%', md: '80%' }
      }}>
        {/* Titre principal - "SCOLARITÉ" */}
        <Typography
          variant="h1"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: 40, md: 60, lg: 80 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
            mb: 2,
            textTransform: 'uppercase',
            letterSpacing: 2
          }}
        >
          SCOLARITÉ
        </Typography>

        {/* Bannière bleue - "CRÈCHE/GARDERIE" */}
        <Box sx={{
          bgcolor: '#1780c2',
          color: 'white',
          py: { xs: 1, md: 1.5 },
          px: { xs: 2, md: 3 },
          borderRadius: 1,
          mb: 2,
          display: 'inline-block',
          border: '2px solid white'
        }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: 18, md: 24, lg: 28 },
              textTransform: 'uppercase',
              letterSpacing: 1
            }}
          >
            MATERNELLE
          </Typography>
        </Box>

        <Typography
          variant="h3"
          sx={{
            color: 'white',
            fontWeight: 400,
            fontSize: { xs: 20, md: 28, lg: 36 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
            textTransform: 'uppercase',
            letterSpacing: 1
          }}
        >
          ÉDUCATION PRÉSCOLAIRE
        </Typography>
      </Box>
    </Box>
  );
};

// Section Crèche/Garderie avec informations et inscription
const CrecheGarderieSection = () => {
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      minHeight: { xs: '80vh', md: '100vh' },
      background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 25%, #fff3e0 50%, #e8f5e8 75%, #fce4ec 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 4, md: 6 }
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          {/* Colonne gauche - Texte */}
          <Grid item xs={12} md={6}>

            <Typography
              variant="body1"
              sx={{
                color: '#333',
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.7,
                mb: 2,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              La maternelle est le premier pas vers le monde scolaire. À La Maison des Enfants - La petite académie, nous accueillons les enfants de{' '}
              <Box component="span" sx={{
                color: '#4CAF50',
                fontWeight: 700,
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                px: 0.5,
                borderRadius: 0.5
              }}>
                2 ans et demi à 5 ans
              </Box>
              {' '}dans un cadre sécurisé et chaleureux.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#333',
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.7,
                mb: 4,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              Grâce à des méthodes pédagogiques adaptées, vos enfants acquièrent les bases de la lecture, de l'écriture, du langage et des mathématiques, tout en développant leur curiosité et leur autonomie.
            </Typography>


            {/* Description */}

          </Grid>

          {/* Colonne droite - Image */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <Box sx={{
                position: 'relative',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                maxWidth: '100%',
                width: '100%',
                height: { xs: 300, md: 500 }
              }}>
                <img
                  src="/img/pages/scolaritema.png"
                  alt="Enfants dans la crèche/garderie"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Section Fiche de renseignements
const FicheRenseignementsSection = () => {
  const pdfRef = useRef<HTMLDivElement>(null);

  const handleDownloadPDF = async () => {
    if (!pdfRef.current) return;
    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#fff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save('Fiche-Renseignement-Maternelle-2026-2027.pdf');
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 4, md: 6 }
    }}>
      <Container maxWidth="lg">
        <Box ref={pdfRef} sx={{
          bgcolor: 'white',
          borderRadius: 0,
          p: { xs: 3, md: 5 },
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          maxWidth: '100%',
          border: '1px solid #ccc'
        }}>
          {/* En-tête */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
            <Box sx={{ width: 120, height: 120 }}>
              <img
                src="/img/pages/logologo.webp"
                alt="Logo La Maison des Enfants"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1, pt: 3 }}>
              <Typography variant="h3" sx={{ color: '#333', fontWeight: 700, mb: 1, fontSize: { xs: 24, md: 32 }, fontFamily: 'Times New Roman, serif' }}>
                FICHE DE RENSEIGNEMENT
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', pt: 3 }}>
              <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>
                ANNEE SCOLAIRE 2026-2027
              </Typography>
            </Box>
          </Box>

          {/* Tableau Scolarité */}
          <TableContainer component={Paper} sx={{ mb: 4, boxShadow: 'none', border: '1px solid #333', borderRadius: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e0e0e0' }}>
                  <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>1er<br />versement<br />lors de<br />l'inscription</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>2ème<br />versement<br />début sept.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>3ème<br />versement<br />début nov.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>4ème<br />versement<br />début fév.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #333' }}>TOTAL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>TPS (année de naissance 2024)<br />PS (année de naissance 2023)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>300.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>300.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>300.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333' }}>250.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #333' }}>1.150.000</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #333' }}>MS (année de naissance 2022)<br />GS (année de naissance 2021)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333' }}>300.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333' }}>350.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333' }}>320.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333' }}>300.000</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>1.270.000</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Inscription et Reinscription */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textDecoration: 'underline', textAlign: 'center', fontSize: '1rem' }}>
            INSCRIPTION ET REINSCRIPTION
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <TableContainer component={Paper} sx={{ width: { xs: '100%', md: '80%' }, boxShadow: 'none', border: '1px solid #333', borderRadius: 0 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #333', borderRight: '1px solid #333' }}>Frais d'inscription (obligatoire)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #333' }}>100.000f CFA</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #333', borderRight: '1px solid #333' }}>Frais de réinscription (obligatoire)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #333' }}>60.000f CFA</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #333' }}>Projet d'école (visite et sortie pédagogique)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>40.000f CFA</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <List sx={{ pt: 0, pb: 2, pl: 4, pr: 2 }}>
            <ListItem sx={{ py: 0, display: 'list-item', listStyleType: 'none', pl: 0 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', display: 'flex' }}>
                <span style={{ marginRight: '8px' }}>➢</span>
                Cours d'anglais de la Toute petite section à la grande section.
              </Typography>
            </ListItem>
            <ListItem sx={{ py: 0, display: 'list-item', listStyleType: 'none', pl: 0 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', display: 'flex' }}>
                <span style={{ marginRight: '8px' }}>➢</span>
                Cours de robotique et de natation (février -mars - : La Moyenne section et la grande section:
              </Typography>
            </ListItem>
          </List>

          <Typography sx={{ mb: 4, textAlign: 'center', fontSize: '0.85rem', fontWeight: 600 }}>
            L'inscription d'un enfant n'est effective qu'après le règlement des droits d'inscription (frais d'inscription+ frais annexes) et du 1er versement (non remboursables)
          </Typography>

          {/* Paiement */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textDecoration: 'underline', textAlign: 'center', fontSize: '1rem' }}>
            PAIEMENT
          </Typography>
          <Typography sx={{ mb: 2, fontSize: '0.9rem' }}>
            Pour le dépôt à la banque, il faut prévoir la somme de 100 FCFA pour les frais de timbre.
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 1, boxShadow: 'none', border: '1px solid #333', borderRadius: 0 }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '20%' }}>CHEQUE</TableCell>
                  <TableCell colSpan={5} sx={{ fontWeight: 'bold', borderBottom: '1px solid #333' }}>A L'ORDRE DE LA MAISON DES ENFANTS-LA PETITE ACADEMIE</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #333' }}>DEPOT BANCAIRE</TableCell>
                  <TableCell sx={{ borderRight: '1px solid #333', fontWeight: 'bold' }}>CI032</TableCell>
                  <TableCell sx={{ borderRight: '1px solid #333', fontWeight: 'bold' }}>01014</TableCell>
                  <TableCell sx={{ borderRight: '1px solid #333', fontWeight: 'bold' }}>006383560002</TableCell>
                  <TableCell sx={{ borderRight: '1px solid #333', fontWeight: 'bold' }}>50</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>BANK OF AFRICA COTE D'IVOIRE</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center', textDecoration: 'underline', fontSize: '0.9rem' }}>
            Frais de chèques impayés : 20.000f CFA
          </Typography>

          <Typography sx={{ mb: 1, textAlign: 'justify', fontSize: '0.9rem' }}>
            Toujours préciser sur le bordereau du versement le nom, le prénom et la classe de l'enfant. Se présenter avec le justificatif du versement à l'école ou le transmettre par mail ou par WhatsApp dans un bref délai au service comptable (infos@lapetiteacademie.ci) afin de valider le paiement et rendre effective l'admission en classe.
          </Typography>

          <Typography sx={{ mb: 4, fontWeight: 'bold', display: 'flex', alignItems: 'flex-start', fontSize: '0.9rem' }}>
            <span style={{ marginRight: '8px', marginLeft: '32px' }}>➢</span>
            Une réduction de la scolarité de 05%, hors frais d'inscription, est accordée aux familles à partir du 2ème enfant.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 2, fontSize: '1rem' }}>
                LISTE DES PIECES OBLIGATOIRES A FOURNIR
              </Typography>
              <Box sx={{ pl: 0 }}>
                <Typography sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>- 1 extrait de naissance original</Typography>
                <Typography sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>- 2 photos d'identité récentes de même tirage</Typography>
                <Typography sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>- 1 photocopie du carnet de vaccination à jour</Typography>
                <Typography sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>- 1 Fiche d'Identification à récupérer à la petite Académie</Typography>
                <Typography sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>- 1 fiche Sanitaire à récupérer à la Petite Académie</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 2, textAlign: 'center', fontSize: '1rem' }}>
                Fournitures
              </Typography>
              <Box sx={{ pl: { xs: 0, md: 4 } }}>
                <Typography sx={{ mb: 1, fontSize: '0.9rem' }}>1. Grande section : <strong>95.000f CFA</strong></Typography>
                <Typography sx={{ mb: 1, fontSize: '0.9rem' }}>2. Moyenne section : <strong>85.000f CFA</strong></Typography>
                <Typography sx={{ mb: 1, fontSize: '0.9rem' }}>3. Petite section : <strong>80.000f CFA</strong></Typography>
                <Typography sx={{ mb: 2, fontSize: '0.9rem' }}>4. Toute petite section : <strong>75.000f CFA</strong></Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Tenues scolaires: 30.000 le kit (2 uniformes et une tenue de sport)</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Cantine */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textDecoration: 'underline', textAlign: 'center', fontSize: '1rem' }}>
            CANTINE (facultative)
          </Typography>
          <Typography sx={{ mb: 2, fontWeight: 'bold', textDecoration: 'underline', fontSize: '0.9rem' }}>
            Le paiement se fait au début de chaque période bimestrielle
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 4, boxShadow: 'none', border: '1px solid #333', borderRadius: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ fontWeight: 'bold', bgcolor: '#e0e0e0', borderBottom: '1px solid #333' }}>MONTANT(F CFA)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>SEP.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>OCT.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>NOV.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>DEC.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>JAN.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>FEV.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>MARS</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>AVR.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>MAI</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', borderBottom: '1px solid #333', width: '9%' }}>JUIN</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #333', width: '10%' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', py: 2 }}>100.000</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', py: 2 }}>100.000</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', py: 2 }}>100.000</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', py: 2 }}>100.000</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #333', py: 2 }}>100.000</TableCell>
                  <TableCell colSpan={1} align="center" sx={{ fontWeight: 'bold', py: 2, bgcolor: '#e0e0e0' }}>500.000</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, pt: 2, bgcolor: '#1780c2', color: 'white', px: 2, pb: 2 }}>
            <Typography sx={{ fontSize: '0.85rem', mb: 1 }}>
              Pour tout enfant non inscrit à la cantine mais restant toute la journée, un droit d'hébergement de 15.000 FCFA est à payer chaque mois.
            </Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#ff9800' }}>
              Crèche maternelle la Maison des enfants-la Petite Académie - Riviera3, Cité PALM-INSDUSTRIE, carrefour BON PASTEUR
            </Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#ff9800' }}>
              CONTACTS : 27 22 51 69 04 / 07 08 02 24 24 / Email : infos@lapetiteacademie.ci
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleDownloadPDF}
            sx={{
              bgcolor: '#1780c2',
              color: 'white',
              fontSize: { xs: 14, md: 16 },
              fontWeight: 600,
              py: 2,
              px: 4,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                bgcolor: '#1565c0',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Télécharger la Fiche (PDF)
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

// Composant principal Scolarité
const Scolarite = () => {
  return (
    <>
      <Helmet>
        <title>Crèche Garderie TPS Abidjan - La Petite Académie | Inscription 2025-2026</title>
        <meta name="description" content="Crèche et garderie TPS à Abidjan Cocody Riviera 3. La Petite Académie accueille les tout-petits de 3 mois à 3 ans dans un environnement bienveillant avec méthode Montessori et programme bilingue français-anglais. Inscription 2025-2026." />
        <meta name="keywords" content="crèche TPS Abidjan, garderie TPS Cocody, crèche 3 mois 3 ans, garderie bébé Abidjan, méthode Montessori crèche, programme bilingue crèche, inscription crèche 2025, La Petite Académie, crèche privée Riviera 3, éveil bébé, développement tout-petit" />
        <meta property="og:title" content="Crèche Garderie TPS Abidjan - La Petite Académie | Inscription 2025-2026" />
        <meta property="og:description" content="Crèche et garderie TPS à Abidjan Cocody Riviera 3. La Petite Académie accueille les tout-petits de 3 mois à 3 ans avec méthode Montessori et programme bilingue." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lapetiteacademie.ci/scolarite" />
        <link rel="canonical" href="https://lapetiteacademie.ci/scolarite" />
      </Helmet>
      <Box sx={{
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <ContactBanner />
        <NavigationBar />
        <ScolariteHero />
        <CrecheGarderieSection />
        <FicheRenseignementsSection />

        <SiteFooter />
      </Box>
    </>
  );
};

export default Scolarite;
